import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useAuth } from "../../../hooks";
import { useState } from "react";
import { validateEmail } from "../../../helpers";
import { BiEnvelope, BiLockOpen, BiUser } from "react-icons/bi";
import PhonePrefixSelect, { COUNTRY_CODES } from "./PhonePrefixSelect";

type FormFields = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  handleClose: () => void;
  handleSuccess: () => void;
};

const CustomerModal: React.FC<Props> = ({
  show,
  handleSuccess,
  handleClose,
}) => {
  const { customer, setCustomer } = useAuth();

  const [_, setValidated] = useState<boolean>(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormFields>({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    if (["first_name", "last_name"].includes(name))
      value = value.replace(/[^a-zA-Z\s]/g, "");
    else if (["phone"].includes(name)) value = value.replace(/[^0-9]/g, "");

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear the error when user types
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    setValidated(false);

    if (!formData.first_name) {
      newErrors.first_name = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length != 10) {
      newErrors.phone = "Phone number must be of 10 digits";
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return false;

    setCustomer({
      ...customer,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
    });

    handleSuccess();
  };

  return (
    <Modal
      className="login modal-width modal-mobile-bottom"
      show={show}
      onHide={handleClose}
      fullscreen="sm-down"
      centered
    >
      <Form method="POST" onSubmit={handleSubmit}>
        <Modal.Header closeButton className="login-modal-header border-0 sticky-modal-header">
          <Modal.Title className="login-modal-title">My Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="login_main">
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">First Name</Form.Label>
                <div className="address_icon">
                  <span>
                    <BiUser size={20} />
                  </span>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    isInvalid={!!errors.first_name}
                    required
                  />
                </div>
                {errors.first_name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.first_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">Last Name</Form.Label>
                <div className="address_icon">
                  <span>
                    <BiUser size={20} />
                  </span>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    isInvalid={!!errors.last_name}
                  />
                </div>
                {errors.last_name && (
                  <Form.Control.Feedback type="invalid">
                    {errors.last_name}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            {/* <Col sm={12} className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <InputGroup>
                <InputGroup.Text>+1</InputGroup.Text>
                <Form.Control
                  type="tel"
                  name="phone"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  required
                />
                {errors.phone && (
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                )}
              </InputGroup>
            </Col> */}

            <Col sm={12}>
              <Form.Group className="auth-group mb-3">
                <Form.Label className="login-label">Phone Number</Form.Label>

                <div className="phone_field_wrapper">
                  {/* PREFIX */}
                  <PhonePrefixSelect value={COUNTRY_CODES[0]} />
                  <Form.Control 
                   type="tel" 
                   placeholder="(201) 555-0123" 
                   name="phone" 
                   maxLength={10} 
                   value={formData.phone}
                   onChange={handleChange}
                   isInvalid={!!errors.phone} 
                   required 
                   className="phone-control" />

                  {/* HELPER TEXT */}
                  <div className="input-helper-text">
                    We’ll use this to keep you updated about your order.
                  </div>
                </div>

                {errors.phone && (
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            
            <Col sm={12}>
              <Form.Group className="mb-3">
                <Form.Label className="login-label">Email Address</Form.Label>
                <div className="address_icon">
                  <span>
                    <BiEnvelope size={20} />
                  </span>
                  <Form.Control
                    type="email"
                    name="email"
                    defaultValue={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    required
                  />
                </div>
                {errors.email && (
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="w-100 login_btn"
            type="submit"
            onClick={handleSubmit}
          >
            Continue
          </Button>
          <div className="d-flex justify-content-center align-items-center w-100 modal-bottom-text">
            <BiLockOpen size={22} />
            We keep your information secure and private
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerModal;
